/// <reference types="google.maps" />
import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Account } from '../../../shared/models';
import { CampaignSurvey } from '../../../shared/models/campaign-survey';
import { OnboardingService } from '../../../shared/onboarding.service';
import { VerticalService } from '../../../shared/vertical.service';
import { finalize, take } from 'rxjs/operators';
import { Vertical } from '../../../shared/models/vertical';
import { GoogleMapsLoaderService } from '../../../shared/google-maps-loader.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-campaign-survey',
  templateUrl: './campaign-survey.component.html',
  styleUrl: './campaign-survey.component.scss'
})
export class CampaignSurveyComponent implements OnInit {
  loading = false;
  form: FormGroup;
  verticals: Vertical[];
  @Input() account: Account;
  @Input() survey: CampaignSurvey;

  private isInitialized = false;
  private _inputElement!: ElementRef;

  constructor(
    public activeModal: NgbActiveModal,
    private onboardingService: OnboardingService,
    private verticalService: VerticalService,
    private mapsLoader: GoogleMapsLoaderService,
    private translateService: TranslateService
  ) { }

  @ViewChild('placeInput') set placeInput(el: ElementRef) {
      if (el && !this.isInitialized) {
        this._inputElement = el;
        this.configureWidget();
      }
  }

  ngOnInit(): void {
    this.loading = true;
    this.initForm();

    this.verticalService.all(this.translateService.currentLang).pipe(
      take(1),
      finalize(() => this.loading = false)
    ).subscribe(verticals => {
      this.verticals = verticals;

      // update vertical if present
      if (this.survey?.vertical?.verticalId) {
        let selected: Vertical | undefined;
        selected = this.verticals.find(v => v.verticalId === this.survey.vertical.verticalId);
        if (selected) {
          this.form.patchValue({ vertical: selected });
        }
      }
    });
  }

  async onPlaceSelect(event: any) {
    if (!event.placePrediction) {
      return;
    }

    const place = event.placePrediction.toPlace();

    await place.fetchFields({
      fields: ['displayName', 'formattedAddress', 'addressComponents']
    });

    const getComponent = (type: string) => {
      return place.addressComponents.find(
        (component: any) => component.types.includes(type)
      );
    };

    const cityComponent = getComponent('locality');
    const stateComponent = getComponent('administrative_area_level_1');
    const countryComponent = getComponent('country');

    // Use shortText for abbreviation, longText for full name
    const city = cityComponent?.longText || '';
    const state = stateComponent?.shortText || '';
    const stateLong = stateComponent?.longText || '';
    const country = countryComponent?.longText || '';

    if (city !== '' && state !== '') {
      this.form.patchValue({ location: `${city}, ${state}` });
    } else if (stateLong !== '' && country !== '') {
      this.form.patchValue({ location: `${stateLong}, ${country}` });
    } else {
      console.error('Could not extract city and state or state and country from the selected place.');
    }
  }

  async configureWidget() {
    this.isInitialized = true;
    try {
      await this.mapsLoader.load();
      await google.maps.importLibrary('places');
    } catch (e) {
      console.error('Setup Error', e);
    }
  }

  submit(): void {
    this.loading = true;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.loading = false;
      return;
    }

    this.onboardingService.saveCampaignSurvey(this.account.id, this.form.getRawValue())
      .pipe(finalize(() => {
        this.loading = false;
        this.activeModal.close();
      })).subscribe();
  }

  private initForm(): void {
    this.form = new FormGroup({
      domain: new FormControl(null, Validators.required),
      vertical: new FormControl(null, Validators.required),
      businessOverview: new FormControl(null, Validators.required),
      blogRestrictions: new FormControl(null),
      location: new FormControl(null, Validators.required),
      product: new FormControl(null, Validators.required),
      additionalProducts: new FormControl(null),
    });

    if (this.survey) {
      this.form.patchValue(this.survey);

      // Disable controls for any field that already has a value
      Object.keys(this.form.controls).forEach(key => {
        const value = this.survey[key];
        if (value !== null && value !== undefined && value !== '') {
          this.form.get(key).disable();
        }
      });
    }
  }
}
